import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { reelsActions, reelsItemsSelector } from '$store/reels/index';
import { Api } from '$api';
import { batchActions } from '$store';
import { mainActions } from '$store/main';
import {
  DeleteReelAction,
  LoadListAction,
  LoadReelsAction,
  SendViewAction,
  SetTabBadgeAction,
  ToggleLikeAction,
  RestoreReelAction,
  ReportReelAction,
} from '$store/reels/interface';
import router from '$navigation/router';
import { removeStorageItem, setStorageItem } from '$utils';
import { toastActions } from '$store/toast';

function* loadReelsWorker(action: LoadReelsAction) {
  try {
    const { params } = yield call(router.getState);
    const reelId = params.reel_id || 0;
    const existReels = yield select(reelsItemsSelector);
    let skipIds: number[] = [];
    for (let reel of existReels) {
      skipIds.push(reel.id);
    }

    if (skipIds.length > 50) {
      skipIds = skipIds.slice(-50);
    }

    const data = yield call(Api.post, '/reels', {
      reelId: action.payload.replace ? +reelId : 0,
      skipIds,
    });
    const reels = yield select(reelsItemsSelector);
    const exists: { [index: number]: boolean } = {};
    for (let reel of reels) {
      exists[reel.id] = true;
    }

    const newReels = [...reels];
    for (let reel of data.reels) {
      if (exists[reel.id]) {
        continue;
      }
      exists[reel.id] = true;
      newReels.push(reel);
    }

    yield put(
      batchActions(
        mainActions.setUsers(data.users),
        reelsActions.setReels(newReels),
        reelsActions.setCanUploadReel(data.canUploadReel),
        reelsActions.setUserFollows(data.userFollows),
      ),
    );
  } catch (e) {
    yield put(reelsActions.setReelsFailed());
  }
}

function* toggleLikeWorker(action: ToggleLikeAction) {
  const { reelId, isLiked, duration = 0, likesCount = 0, ownerId } = action.payload;
  try {
    yield put(
      reelsActions.updateReel({
        ownerId,
        reelId,
        updated: {
          isLiked: isLiked,
          likes: isLiked ? likesCount + 1 : Math.max(0, likesCount - 1),
        },
      }),
    );
    if (isLiked) {
      yield call(Api.post, `/reels/${reelId}/like`, {
        duration,
      });
    } else {
      yield call(Api.post, `/reels/${reelId}/unlike`);
    }
  } catch (e) {
    yield put(
      reelsActions.updateReel({
        ownerId,
        reelId,
        updated: {
          isLiked: !isLiked,
          likes: likesCount,
        },
      }),
    );
  }
}

function* sendViewWorker(action: SendViewAction) {
  try {
    const { reelId, duration } = action.payload;
    yield call(Api.post, `/reels/${reelId}/view`, {
      duration,
    });
  } catch (e) {}
}

function* setTabBadgeWorker(action: SetTabBadgeAction) {
  try {
    if (action.payload) {
      yield call(removeStorageItem, 'reelsBadgeHidden');
    } else {
      yield call(setStorageItem, 'reelsBadgeHidden', 'true');
    }
  } catch (e) {}
}

function* loadListWorker(action: LoadListAction) {
  const { list, startFrom, replace = true } = action.payload;
  try {
    const data = yield call(Api.post, '/reels/list', {
      list,
      ...(startFrom !== undefined && { startFrom }),
    });
    yield put(
      batchActions(
        reelsActions.setListLoadingStatus({
          list,
          status: '',
        }),
        reelsActions.setList({
          list,
          reels: data.reels,
          startFrom: data.nextFrom,
          replace,
        }),
        reelsActions.setUserFollows(data.userFollows),
        mainActions.setUsers(data.users),
      ),
    );
  } catch (e) {
    yield put(
      reelsActions.setListLoadingStatus({
        list,
        status: 'failed',
      }),
    );
  }
}

function* deleteReelWorker(action: DeleteReelAction) {
  const { reelId, refreshReelsId } = action.payload;
  yield put(toastActions.setToastLoading());
  try {
    yield call(Api.post, `/reels/${reelId}/delete`);
    yield put(toastActions.setToastSuccess('Reel deleted'));
    yield put(reelsActions.setDeleteReel({ reelId, isDelete: true }));
    if (refreshReelsId) {
      yield put(reelsActions.loadList({ list: `profile_${refreshReelsId}` }));
    }
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* restoreReelWorker(action: RestoreReelAction) {
  const reelId = action.payload;
  yield put(toastActions.setToastLoading());
  try {
    yield call(Api.post, `/reels/${reelId}/restore`);
    yield put(toastActions.setToastSuccess('Reel restored'));
    yield put(reelsActions.setDeleteReel({ reelId, isDelete: false }));
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* reportReelWorker(action: ReportReelAction) {
  const { reelId, reason } = action.payload;
  yield put(toastActions.setToastLoading());
  try {
    yield call(Api.post, `/report`, {
      objectType: 'reels',
      objectId: reelId,
      reason,
    });
    yield put(toastActions.setToastSuccess('Reel reported'));
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

export function* reelsSaga() {
  yield all([
    takeLatest(reelsActions.loadReels, loadReelsWorker),
    takeLatest(reelsActions.toggleLike, toggleLikeWorker),
    takeLatest(reelsActions.sendView, sendViewWorker),
    takeLatest(reelsActions.setTabBadge, setTabBadgeWorker),
    takeLatest(reelsActions.loadList, loadListWorker),
    takeLatest(reelsActions.deleteReel, deleteReelWorker),
    takeLatest(reelsActions.restoreReel, restoreReelWorker),
    takeLatest(reelsActions.reportReel, reportReelWorker),
  ]);
}
